/**
 * MACRO #26 · Increment 3 — Premium Canvas Showcase Templates.
 *
 * Six genuinely distinct professional starting points. A template is a pure
 * function of the owner's real Territory geometry → a set of canonical
 * `CanvasElement`s + `CanvasStyle`, expressed ONLY with supported Canvas
 * modules (text / link / social / contacts / shapes) and approved Grid colour /
 * font tokens. NO media asset ids, NO pixel art, NO fabricated components — so a
 * template is always valid under the certified Canvas catalogue + validation and
 * carries no permanent fake user content.
 *
 * Coordinates are authored in a normalized 0..100 space and mapped into the
 * territory's real bounding box (with padding) at apply time, so a template
 * scales to any owned holding. Applying a template only POPULATES THE DRAFT
 * (the editor working state) — Save / Publish stay in the owner's hands.
 */
import {
  tempId,
  DEFAULT_SOCIAL_STYLE,
  DEFAULT_CONTACTS_STYLE,
  DEFAULT_LINK_STYLE,
  type CanvasElement,
  type CanvasStyle,
  type TerritoryGeometry,
} from "../../domain/canvas-model";

export type TemplateId =
  | "creator" | "retail" | "gallery" | "music" | "product" | "minimal";

export type Accent = "teal" | "coral" | "gold" | "violet" | "blue" | "mist";

/** A single normalized element spec (x/y/w/h in 0..100 of the padded bounds). */
export interface Spec {
  kind:
    | "title" | "tagline" | "description"
    | "link" | "social" | "contacts"
    | "rect" | "round" | "ellipse" | "line";
  x: number; y: number; w: number; h: number;
  text?: string;
  font?: string;
  color?: string;
  align?: "LEFT" | "CENTER" | "RIGHT";
  bold?: boolean;
  fill?: string;
  glow?: "OFF" | "LOW" | "MEDIUM" | "HIGH";
  glowColor?: string;
  label?: string;
  url?: string;
  subtitle?: string;
  linkPreset?: "TEXT" | "BUTTON" | "OUTLINE" | "CARD";
  opacity?: number;
}

export interface TemplateDef {
  id: TemplateId;
  name: string;
  useCase: string;
  tagline: string;
  tags: string[];
  accent: Accent;
  style: CanvasStyle;
  specs: Spec[];
}

export const ACCENT_HEX: Record<Accent, string> = {
  teal: "#2DB6F0",
  coral: "#FF4757",
  gold: "#f6c343",
  violet: "#8b5cf6",
  blue: "#2f7bff",
  mist: "#dfe6ef",
};

// ── Templates (normalized authoring) ───────────────────────────────────────

const CREATOR: TemplateDef = {
  id: "creator",
  name: "Creator / Personal Brand",
  useCase: "Personal identity",
  tagline: "A bold centred identity — name, role, bio, socials and a single call to action.",
  tags: ["Identity", "Bio", "Socials"],
  accent: "teal",
  style: { background_mode: "SOLID_COLOR", background_color: "GRID_OBSIDIAN", perimeter_color: "GRID_CYAN", perimeter_glow: "MEDIUM" },
  specs: [
    { kind: "round", x: 40, y: 6, w: 20, h: 20, fill: "GRID_GRAPHITE", glow: "LOW", glowColor: "GRID_CYAN" },
    { kind: "title", x: 12, y: 30, w: 76, h: 14, text: "YOUR NAME", font: "GRID_DISPLAY", color: "GRID_WHITE", align: "CENTER", bold: true },
    { kind: "tagline", x: 20, y: 46, w: 60, h: 6, text: "Creator · Designer · Storyteller", font: "GRID_MONO", color: "GRID_CYAN", align: "CENTER" },
    { kind: "description", x: 18, y: 55, w: 64, h: 16, text: "One line about who you are and what you make. Keep it human, keep it short — this is your first impression on the Grid.", font: "GRID_SANS", color: "GRID_SILVER", align: "CENTER" },
    { kind: "social", x: 30, y: 74, w: 40, h: 8 },
    { kind: "link", x: 34, y: 86, w: 32, h: 8, label: "Work with me", url: "https://example.com", linkPreset: "BUTTON" },
  ],
};

const RETAIL: TemplateDef = {
  id: "retail",
  name: "Retail / Storefront",
  useCase: "Small brand & shop",
  tagline: "A brand header over a rhythmic product shelf, with a clear visit-shop action.",
  tags: ["Products", "Shelf", "Shop link"],
  accent: "gold",
  style: { background_mode: "SOLID_COLOR", background_color: "GRID_GRAPHITE", perimeter_color: "GRID_GOLD", perimeter_glow: "LOW" },
  specs: [
    { kind: "title", x: 6, y: 6, w: 60, h: 10, text: "BRAND STUDIO", font: "GRID_BOLD", color: "GRID_WHITE", align: "LEFT", bold: true },
    { kind: "tagline", x: 6, y: 17, w: 60, h: 5, text: "Objects made to last.", font: "GRID_SANS", color: "GRID_GOLD", align: "LEFT" },
    { kind: "line", x: 6, y: 26, w: 88, h: 0, color: "GRID_STEEL" },
    { kind: "round", x: 6, y: 32, w: 26, h: 34, fill: "GRID_SLATE" },
    { kind: "round", x: 37, y: 32, w: 26, h: 34, fill: "GRID_SLATE" },
    { kind: "round", x: 68, y: 32, w: 26, h: 34, fill: "GRID_SLATE" },
    { kind: "tagline", x: 6, y: 68, w: 26, h: 5, text: "Piece 01 · €120", font: "GRID_MONO", color: "GRID_MIST", align: "CENTER" },
    { kind: "tagline", x: 37, y: 68, w: 26, h: 5, text: "Piece 02 · €90", font: "GRID_MONO", color: "GRID_MIST", align: "CENTER" },
    { kind: "tagline", x: 68, y: 68, w: 26, h: 5, text: "Piece 03 · €150", font: "GRID_MONO", color: "GRID_MIST", align: "CENTER" },
    { kind: "link", x: 6, y: 82, w: 40, h: 10, label: "Visit the shop", url: "https://example.com", linkPreset: "BUTTON" },
    { kind: "contacts", x: 52, y: 80, w: 42, h: 14 },
  ],
};

const GALLERY: TemplateDef = {
  id: "gallery",
  name: "Artist / Gallery",
  useCase: "Artwork-first",
  tagline: "A single dominant frame with a quiet statement — the work leads, words follow.",
  tags: ["Artwork", "Statement", "Minimal"],
  accent: "violet",
  style: { background_mode: "SOLID_COLOR", background_color: "GRID_BLACK", perimeter_color: "GRID_VIOLET", perimeter_glow: "LOW" },
  specs: [
    { kind: "rect", x: 6, y: 8, w: 56, h: 72, fill: "GRID_GRAPHITE", glow: "LOW", glowColor: "GRID_VIOLET" },
    { kind: "rect", x: 10, y: 12, w: 48, h: 64, fill: "GRID_SLATE" },
    { kind: "title", x: 66, y: 12, w: 28, h: 10, text: "UNTITLED", font: "GRID_ELEGANT", color: "GRID_WHITE", align: "LEFT" },
    { kind: "tagline", x: 66, y: 23, w: 28, h: 5, text: "Mixed media · 2026", font: "GRID_MONO", color: "GRID_VIOLET", align: "LEFT" },
    { kind: "description", x: 66, y: 32, w: 28, h: 34, text: "A short artist statement. Let the work breathe — say only what the piece cannot say for itself.", font: "GRID_SERIF", color: "GRID_SILVER", align: "LEFT" },
    { kind: "social", x: 66, y: 84, w: 28, h: 8 },
    { kind: "title", x: 6, y: 84, w: 50, h: 8, text: "THE ARTIST", font: "GRID_ELEGANT", color: "GRID_MIST", align: "LEFT" },
  ],
};

const MUSIC: TemplateDef = {
  id: "music",
  name: "Music / Event",
  useCase: "Release & live event",
  tagline: "High-energy title, date and place, and a direct tickets / stream action.",
  tags: ["Event", "Date", "Tickets"],
  accent: "coral",
  style: { background_mode: "SOLID_COLOR", background_color: "GRID_BLACK", perimeter_color: "GRID_CORAL", perimeter_glow: "HIGH" },
  specs: [
    { kind: "rect", x: 0, y: 30, w: 100, h: 26, fill: "GRID_MAROON", opacity: 0.55 },
    { kind: "title", x: 6, y: 20, w: 88, h: 22, text: "NIGHT SIGNAL", font: "GRID_DISPLAY", color: "GRID_WHITE", align: "LEFT", bold: true },
    { kind: "tagline", x: 6, y: 46, w: 88, h: 7, text: "SAT 12 SEP · 22:00 · WAREHOUSE 9", font: "GRID_MONO", color: "GRID_CORAL", align: "LEFT" },
    { kind: "description", x: 6, y: 58, w: 60, h: 14, text: "Live set, visuals and one long night. Doors at ten, first drop at eleven.", font: "GRID_SANS", color: "GRID_SILVER", align: "LEFT" },
    { kind: "link", x: 6, y: 78, w: 34, h: 12, label: "Get tickets", url: "https://example.com", linkPreset: "BUTTON" },
    { kind: "social", x: 60, y: 80, w: 34, h: 8 },
  ],
};

const PRODUCT: TemplateDef = {
  id: "product",
  name: "Product / Tech Launch",
  useCase: "Launch landing",
  tagline: "A modern launch page — headline, supporting line, three feature blocks and a CTA.",
  tags: ["Launch", "Features", "CTA"],
  accent: "blue",
  style: { background_mode: "SOLID_COLOR", background_color: "GRID_OBSIDIAN", perimeter_color: "GRID_ELECTRIC_BLUE", perimeter_glow: "MEDIUM" },
  specs: [
    { kind: "title", x: 6, y: 8, w: 70, h: 14, text: "SHIP FASTER", font: "GRID_DISPLAY", color: "GRID_WHITE", align: "LEFT", bold: true },
    { kind: "tagline", x: 6, y: 24, w: 66, h: 8, text: "The launch surface for your next release.", font: "GRID_SANS", color: "GRID_AZURE", align: "LEFT" },
    { kind: "round", x: 6, y: 40, w: 27, h: 30, fill: "GRID_GRAPHITE", glow: "LOW", glowColor: "GRID_ELECTRIC_BLUE" },
    { kind: "round", x: 36, y: 40, w: 27, h: 30, fill: "GRID_GRAPHITE", glow: "LOW", glowColor: "GRID_ELECTRIC_BLUE" },
    { kind: "round", x: 66, y: 40, w: 27, h: 30, fill: "GRID_GRAPHITE", glow: "LOW", glowColor: "GRID_ELECTRIC_BLUE" },
    { kind: "tagline", x: 9, y: 45, w: 21, h: 6, text: "Fast", font: "GRID_MONO", color: "GRID_WHITE", align: "CENTER" },
    { kind: "tagline", x: 39, y: 45, w: 21, h: 6, text: "Secure", font: "GRID_MONO", color: "GRID_WHITE", align: "CENTER" },
    { kind: "tagline", x: 69, y: 45, w: 21, h: 6, text: "On-chain", font: "GRID_MONO", color: "GRID_WHITE", align: "CENTER" },
    { kind: "link", x: 6, y: 80, w: 36, h: 11, label: "Start now", url: "https://example.com", linkPreset: "BUTTON" },
  ],
};

const MINIMAL: TemplateDef = {
  id: "minimal",
  name: "Minimal Luxury",
  useCase: "High-end minimalism",
  tagline: "Restrained typography and generous space — one word, one line, one action.",
  tags: ["Luxury", "Whitespace", "Restraint"],
  accent: "mist",
  style: { background_mode: "NEUTRAL", perimeter_color: "GRID_WHITE", perimeter_glow: "OFF" },
  specs: [
    { kind: "title", x: 20, y: 38, w: 60, h: 12, text: "MAISON", font: "GRID_ELEGANT", color: "GRID_WHITE", align: "CENTER" },
    { kind: "line", x: 42, y: 54, w: 16, h: 0, color: "GRID_MIST" },
    { kind: "tagline", x: 25, y: 58, w: 50, h: 6, text: "Considered objects, quietly made.", font: "GRID_SERIF", color: "GRID_SILVER", align: "CENTER" },
    { kind: "link", x: 38, y: 70, w: 24, h: 7, label: "Enquire", url: "https://example.com", linkPreset: "TEXT" },
  ],
};

export const TEMPLATES: TemplateDef[] = [CREATOR, RETAIL, GALLERY, MUSIC, PRODUCT, MINIMAL];

export function getTemplate(id: TemplateId): TemplateDef | undefined {
  return TEMPLATES.find((t) => t.id === id);
}

// ── Build (normalized → real world-space canonical elements) ───────────────

function boundsOf(t: TerritoryGeometry): { minX: number; minY: number; W: number; H: number } {
  const b = t.bounds;
  if (b) return { minX: b.min_x, minY: b.min_y, W: Math.max(1, b.max_x - b.min_x), H: Math.max(1, b.max_y - b.min_y) };
  // Fallback: derive from parcels (should not happen for a real territory).
  const ps = t.parcels;
  const minX = Math.min(...ps.map((p) => p.x));
  const minY = Math.min(...ps.map((p) => p.y));
  const maxX = Math.max(...ps.map((p) => p.x + p.w));
  const maxY = Math.max(...ps.map((p) => p.y + p.h));
  return { minX, minY, W: Math.max(1, maxX - minX), H: Math.max(1, maxY - minY) };
}

/** Result: canonical elements + the template's canvas style, fitted to `t`. */
export function buildTemplate(def: TemplateDef, t: TerritoryGeometry): { elements: CanvasElement[]; style: CanvasStyle } {
  const { minX, minY, W, H } = boundsOf(t);
  const padX = W * 0.06;
  const padY = H * 0.06;
  const iw = W - padX * 2;
  const ih = H - padY * 2;
  const fx = (nx: number) => minX + padX + (iw * nx) / 100;
  const fy = (ny: number) => minY + padY + (ih * ny) / 100;
  const fw = (nw: number) => (iw * nw) / 100;
  const fh = (nh: number) => (ih * nh) / 100;
  const clampFont = (v: number) => Math.max(1, Math.min(4000, Math.round(v)));

  const elements: CanvasElement[] = def.specs.map((s, i): CanvasElement => {
    const box = { x: fx(s.x), y: fy(s.y), w: fw(s.w), h: fh(s.h) };
    const base: CanvasElement = {
      element_id: tempId(),
      type: "SHAPE",
      z: i,
      opacity: s.opacity ?? 1,
      hidden: false,
      locked: false,
      glow: s.glow ?? "OFF",
      glow_color: s.glowColor,
      box,
      rotation: 0,
      radius: "SQUARE",
    };

    if (s.kind === "title" || s.kind === "tagline" || s.kind === "description") {
      base.type = s.kind === "title" ? "TITLE" : s.kind === "tagline" ? "TAGLINE" : "DESCRIPTION";
      base.text = s.text ?? "";
      const fontSize = clampFont(box.h * (s.kind === "description" ? 0.28 : 0.72));
      base.text_style = {
        font: s.font ?? "GRID_DISPLAY",
        font_size: fontSize,
        color: s.color ?? "GRID_WHITE",
        align: s.align ?? "LEFT",
        ...(s.bold ? { bold: true } : {}),
      };
      return base;
    }
    if (s.kind === "link") {
      base.type = "LINK";
      base.label = s.label ?? "Open link";
      base.url = s.url ?? "https://example.com";
      base.subtitle = "";
      base.color = "GRID_ELECTRIC_BLUE";
      base.radius = "ROUNDED";
      const linkAlign = s.align === "LEFT" ? "START" : s.align === "RIGHT" ? "END" : "CENTER";
      base.link_style = { ...DEFAULT_LINK_STYLE, preset: s.linkPreset ?? "BUTTON", align: linkAlign };
      return base;
    }
    if (s.kind === "social") {
      base.type = "SOCIAL";
      base.radius = "ROUNDED";
      base.destinations = [
        { platform: "x", url: "https://x.com/" },
        { platform: "instagram", url: "https://instagram.com/" },
        { platform: "website", url: "https://example.com" },
      ];
      base.social_style = { ...DEFAULT_SOCIAL_STYLE, align: "CENTER" };
      return base;
    }
    if (s.kind === "contacts") {
      base.type = "CONTACTS";
      base.radius = "ROUNDED";
      base.entries = [
        { kind: "EMAIL", value: "hello@example.com" },
        { kind: "WEBSITE", value: "https://example.com" },
      ];
      base.contacts_style = { ...DEFAULT_CONTACTS_STYLE };
      return base;
    }
    if (s.kind === "line") {
      base.type = "SHAPE";
      base.shape_subtype = "LINE";
      delete base.box;
      delete base.rotation;
      delete base.radius;
      const y = fy(s.y);
      base.line = { x1: fx(s.x), y1: y, x2: fx(s.x + s.w), y2: y };
      base.line_color = s.color ?? "GRID_WHITE";
      return base;
    }
    // shapes: rect / round / ellipse
    base.type = "SHAPE";
    base.shape_subtype = s.kind === "ellipse" ? "ELLIPSE" : s.kind === "round" ? "ROUNDED_RECTANGLE" : "RECTANGLE";
    base.fill_color = s.fill ?? "GRID_STEEL";
    base.radius = s.kind === "round" ? "ROUNDED" : "SQUARE";
    return base;
  });

  return { elements, style: { ...def.style } };
}
